import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../lib/supabase';

const LOGO_URL = '/images/pkab-horizsm.png';

export default function PaymentLanding() {
  const { itemId } = useParams();
  const [searchParams] = useSearchParams();
  const justPaid = searchParams.get('paid') === '1';

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchItem() {
      const { data, error } = await supabase
        .from('group_order_items')
        .select('*')
        .eq('id', itemId)
        .single();

      if (error || !data) {
        console.error('PaymentLanding fetch error:', error?.message, error?.code, error?.details);
        // If the user just paid (Stripe redirected back with ?paid=1), show success
        // even if we can't load the item (e.g. permissions not yet applied).
        if (!justPaid) setNotFound(true);
      } else {
        setItem(data);
      }
      setLoading(false);
    }
    fetchItem();
  }, [itemId]);

  const amountDollars = item ? (item.item_total_cents / 100).toFixed(2) : '0.00';
  const isPaid = item?.payment_status === 'paid' || justPaid;

  return (
    <div className="min-h-screen bg-warmwhite flex flex-col">
      {/* Header */}
      <header className="bg-charcoal text-white px-6 py-4 flex items-center gap-3">
        <img src={LOGO_URL} alt="Phoenix Kabob" className="h-8 object-contain" onError={e => { e.target.style.display = 'none'; }} />
        <span className="text-sm font-bold tracking-widest text-blush-200 uppercase">Group Order Payment</span>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        {loading && (
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blush-300" />
        )}

        {!loading && notFound && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center space-y-4 max-w-sm">
            <div className="w-20 h-20 bg-sage-50 rounded-full flex items-center justify-center mx-auto">
              <SafeIcon name="AlertCircle" className="text-3xl text-sage-400" />
            </div>
            <h2 className="text-2xl font-serif text-charcoal">Link not found</h2>
            <p className="text-sage-500 text-sm">This payment link may have expired or already been used. Contact your order coordinator if you need a new one.</p>
          </motion.div>
        )}

        {!loading && isPaid && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-6 max-w-sm">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <SafeIcon name="CheckCircle" className="text-5xl text-green-500" />
            </div>
            <h2 className="text-4xl font-serif text-charcoal">You're all paid up!</h2>
            {item ? (
              <p className="text-sage-500">
                Thanks, <strong className="text-charcoal">{item.recipient_name}</strong>. Your payment of <strong className="text-charcoal">${amountDollars}</strong> is confirmed.
              </p>
            ) : (
              <p className="text-sage-500">Your payment is confirmed. Thank you!</p>
            )}
            {item && (
              <div className="glass-panel p-5 text-left space-y-3">
                <div>
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Your order</p>
                  <p className="font-bold text-charcoal mt-1">{item.product_name}</p>
                  {(item.addons || []).length > 0 && (
                    <p className="text-sm text-sage-500 mt-1">
                      + {item.addons.map(a => a.name).join(', ')}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Company</p>
                  <p className="font-bold text-charcoal mt-1">{item.company_name}</p>
                </div>
              </div>
            )}
            {item && (
              <p className="text-xs text-sage-400">A confirmation email is on its way to {item.recipient_email}.</p>
            )}
          </motion.div>
        )}

        {!loading && item && !isPaid && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm space-y-6">
            {/* Order card */}
            <div className="glass-panel p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-blush-50 border border-blush-100 flex items-center justify-center text-2xl shrink-0">
                  🥙
                </div>
                <div>
                  <p className="font-serif text-lg text-charcoal">{item.product_name}</p>
                  <p className="text-sm text-sage-400">{item.company_name} group order</p>
                </div>
              </div>

              {(item.addons || []).length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest mb-2">Add-ons</p>
                  <div className="flex flex-wrap gap-2">
                    {item.addons.map((addon, i) => (
                      <span key={i} className="bg-sage-50 border border-sage-100 text-charcoal px-3 py-1 rounded-full text-xs font-medium">
                        {addon.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-sage-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">Your share</p>
                    <p className="font-serif text-3xl text-charcoal mt-0.5">${amountDollars}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-sage-400 uppercase tracking-widest">For</p>
                    <p className="font-bold text-charcoal mt-0.5">{item.recipient_name}</p>
                  </div>
                </div>
              </div>
            </div>

            {item.checkout_url ? (
              <>
                <a
                  href={item.checkout_url}
                  className="btn-primary w-full flex items-center justify-center gap-3 min-h-[56px] text-lg no-underline"
                >
                  <SafeIcon name="Lock" />
                  Pay ${amountDollars} Now
                </a>
                <p className="text-center text-xs text-sage-400 flex items-center justify-center gap-1">
                  <SafeIcon name="Lock" className="text-xs" />
                  Secured by Stripe · Your card details are never stored on our servers
                </p>
              </>
            ) : (
              <div className="text-center text-sage-400 text-sm py-4">
                <p>Payment link is being generated. Please check back shortly or contact your coordinator.</p>
              </div>
            )}

            <div className="text-center">
              <p className="text-xs text-sage-400">Questions? Email us at{' '}
                <a href="mailto:hi@phoenixkabob.com" className="text-blush-400 font-semibold">hi@phoenixkabob.com</a>
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
